import type { QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { QueueResponse } from "./queue-list";

/**
 * Keeping the Closeout queue current without reloading all of it per click.
 *
 * Nearly every Closeout action (mark a document reviewed, fix a field, add a
 * charge) changes the job's card, and each one used to reload the whole
 * 200-job queue. Every reload counts against the per-person API allowance
 * (`authedApiRateLimiter`), and reviewing quickly was enough to trip "Too many
 * API requests".
 *
 * - `{ jobId }` — the usual case. Re-reads just that job's card
 *   (`/api/close-out/queue?jobId=`, same query and rules as the full queue)
 *   and swaps it into the cached queue in place, so its pills and lane update
 *   as soon as that one small request lands. A job that has left the queue is
 *   dropped from it. Clicks on the same job within `CARD_REFRESH_COALESCE_MS`
 *   share one request.
 * - `{ now: true }` — reload the whole queue immediately. For sends and the
 *   manual refresh, where the next screen depends on the full list.
 * - neither — no job to point at: mark the queue stale and reload it once
 *   after the clicks settle.
 */
export const CLOSE_OUT_QUEUE_KEY = ["close-out-queue"] as const;
export const QUEUE_REFRESH_SETTLE_MS = 1200;
export const CARD_REFRESH_COALESCE_MS = 100;

type Timer = ReturnType<typeof setTimeout>;
const fullPending = new WeakMap<QueryClient, Timer>();
const cardPending = new WeakMap<QueryClient, Map<string, Timer>>();

function clearFull(qc: QueryClient) {
    const t = fullPending.get(qc);
    if (t) clearTimeout(t);
    fullPending.delete(qc);
}

function clearCards(qc: QueryClient) {
    for (const t of cardPending.get(qc)?.values() ?? []) clearTimeout(t);
    cardPending.delete(qc);
}

/** Swap one job's fresh card into the cached queue (or drop it if it left). */
export function patchQueueWithCard(old: QueueResponse | undefined, jobId: string, fresh: QueueResponse) {
    if (!old) return old;
    const card = fresh.jobs.find((j) => j.id === jobId);
    const had = old.jobs.some((j) => j.id === jobId);
    const jobs = card
        ? had
            ? old.jobs.map((j) => (j.id === jobId ? card : j))
            : [...old.jobs, card]
        : old.jobs.filter((j) => j.id !== jobId);
    return { ...old, jobs, myQueue: fresh.myQueue };
}

async function refreshCard(qc: QueryClient, jobId: string): Promise<void> {
    try {
        const fresh = await api.get<QueueResponse>(`/api/close-out/queue?jobId=${encodeURIComponent(jobId)}`);
        qc.setQueryData<QueueResponse>(CLOSE_OUT_QUEUE_KEY, (old) => patchQueueWithCard(old, jobId, fresh));
    } catch {
        // Couldn't read the one card — fall back to the settled full reload.
        refreshCloseOutQueue(qc);
    }
}

export function refreshCloseOutQueue(qc: QueryClient, opts: { now?: boolean; jobId?: string } = {}): Promise<void> {
    if (opts.now) {
        clearFull(qc);
        clearCards(qc);
        return qc.invalidateQueries({ queryKey: CLOSE_OUT_QUEUE_KEY });
    }

    if (opts.jobId) {
        const jobId = opts.jobId;
        const cards = cardPending.get(qc) ?? new Map<string, Timer>();
        cardPending.set(qc, cards);
        const waiting = cards.get(jobId);
        if (waiting) clearTimeout(waiting);
        cards.set(
            jobId,
            setTimeout(() => {
                cards.delete(jobId);
                void refreshCard(qc, jobId);
            }, CARD_REFRESH_COALESCE_MS),
        );
        return Promise.resolve();
    }

    clearFull(qc);
    void qc.invalidateQueries({ queryKey: CLOSE_OUT_QUEUE_KEY, refetchType: "none" });
    fullPending.set(
        qc,
        setTimeout(() => {
            fullPending.delete(qc);
            void qc.refetchQueries({ queryKey: CLOSE_OUT_QUEUE_KEY, type: "active" });
        }, QUEUE_REFRESH_SETTLE_MS),
    );
    return Promise.resolve();
}
